import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { PRIORITIES, PRIORITY_LABELS, TASK_TYPES, TYPE_LABELS, formatMinutes, parseDuration, type TemplateDTO } from '@jynexus/shared';
import { api, ApiError } from '../lib/api.ts';
import { useEpics, useLabels, useProjects, useStories, useTemplates } from '../lib/hooks.ts';
import type { NewTaskDefaults } from './Layout.tsx';
import { StatusBadge } from './badges.tsx';
import { AssigneeSelect, assigneeRef, LabelPicker } from './pickers.tsx';
import { Button, ErrorState, Field, Input, Modal, Select, Textarea, clsx } from './ui.tsx';

interface Candidate {
  key: string;
  title: string;
  status: 'backlog';
  similarity: number;
}

export function NewTaskModal({ defaults, onClose }: { defaults: NewTaskDefaults; onClose: () => void }) {
  const projects = useProjects();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: '',
    projectKey: defaults.projectKey ?? '',
    type: defaults.type ?? 'task',
    priority: 'medium',
    status: defaults.status && ['backlog', 'todo', 'in_progress'].includes(defaults.status) ? defaults.status : 'backlog',
    module: '',
    labels: [] as string[],
    description: '',
    criteria: '',
    dueDate: '',
    assignee: '',
    source: '',
    storyKey: defaults.storyKey ?? '',
    epicKey: defaults.epicKey ?? '',
    startDate: '',
    estimate: '',
  });
  const [duplicates, setDuplicates] = useState<Candidate[] | null>(null);
  const [template, setTemplate] = useState<TemplateDTO | null>(null);
  const templates = useTemplates(form.projectKey || undefined);
  /** Preenche o formulário com o modelo; o servidor soma o checklist do modelo (templateId). */
  const applyTemplate = (t: TemplateDTO | null) => {
    const prev = template;
    setTemplate(t);
    setForm((f) => {
      // Desfaz o que o modelo anterior preencheu e não foi editado.
      const clean = {
        description: prev && f.description === (prev.body ?? '') ? '' : f.description,
        criteria: prev && f.criteria === prev.acceptanceCriteria.join('\n') ? '' : f.criteria,
        labels: prev ? f.labels.filter((l) => !prev.labels.includes(l)) : f.labels,
      };
      if (!t) return { ...f, ...clean };
      return {
        ...f,
        type: t.type,
        priority: t.priority ?? f.priority,
        description: clean.description.trim() ? clean.description : (t.body ?? ''),
        criteria: [clean.criteria.trim(), ...t.acceptanceCriteria].filter(Boolean).join('\n'),
        labels: [...new Set([...clean.labels, ...t.labels])],
        estimate: t.estimateMinutes ? formatMinutes(t.estimateMinutes) : f.estimate,
        title: t.titlePrefix?.trim() && !f.title.startsWith(t.titlePrefix.trim()) ? `${t.titlePrefix.trim()} ${f.title}` : f.title,
      };
    });
  };
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));
  const project = projects.data?.find((p) => p.key === form.projectKey);
  const labels = useLabels(form.projectKey || undefined);
  const stories = useStories(form.projectKey || undefined);
  const epics = useEpics();
  const estimateMinutes = form.estimate.trim() ? parseDuration(form.estimate) : undefined;
  const estimateInvalid = estimateMinutes === null;

  const create = useMutation({
    mutationFn: (confirmNew: boolean) =>
      api.createTask({
        title: form.title,
        projectKey: form.projectKey,
        templateId: template && (!template.projectKey || template.projectKey === form.projectKey) ? template.id : undefined,
        type: form.type,
        priority: form.priority,
        status: form.status,
        module: form.module || undefined,
        labels: form.labels,
        description: form.description || undefined,
        acceptanceCriteria: form.criteria.split('\n').map((s) => s.replace(/^[-*]\s*/, '').trim()).filter(Boolean),
        dueDate: form.dueDate || undefined,
        assignee: assigneeRef(form.assignee) ?? undefined,
        source: form.source || undefined,
        storyKey: !['story', 'epic'].includes(form.type) && form.storyKey ? form.storyKey : undefined,
        epicKey: form.type === 'story' && form.epicKey ? form.epicKey : undefined,
        startDate: form.startDate || undefined,
        estimateMinutes: estimateMinutes ?? undefined,
        confirmNew,
      }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onClose();
      navigate(`/tasks/${t.key}`);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'possible_duplicates') setDuplicates((e.details as { candidates: Candidate[] }).candidates);
    },
  });

  const canSubmit = form.title.trim().length > 0 && !!form.projectKey && !estimateInvalid;
  const submit = (confirmNew = false) => canSubmit && create.mutate(confirmNew);

  return (
    <Modal
      open
      onClose={onClose}
      title="Nova tarefa"
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          {duplicates ? (
            <Button variant="accent" loading={create.isPending} onClick={() => submit(true)}>
              É outro trabalho: criar mesmo assim
            </Button>
          ) : (
            <Button variant="accent" loading={create.isPending} disabled={!canSubmit} onClick={() => submit(false)}>
              Criar tarefa
            </Button>
          )}
        </>
      }
    >
      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          submit(!!duplicates);
        }}
      >
        {!!templates.data?.length && (
          <div className="sm:col-span-2">
            <span className="mb-1 block text-xs font-medium text-slate-600">Modelo</span>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Modelo de tarefa">
              {[null, ...templates.data].map((t) => {
                const active = (template?.id ?? null) === (t?.id ?? null);
                return (
                  <button
                    key={t?.id ?? 'none'}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    title={t?.description}
                    onClick={() => applyTemplate(t)}
                    className={clsx('rounded-full border px-2.5 py-1 text-xs', active ? 'border-navy-600 bg-navy-600 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300')}
                  >
                    {t ? t.name : 'Em branco'}
                  </button>
                );
              })}
            </div>
            {template && template.checklist.length > 0 && <p className="mt-1 text-[11px] text-slate-500">O checklist do modelo ({template.checklist.length} itens) entra ao criar.</p>}
          </div>
        )}
        <Field label="Título" className="sm:col-span-2">
          <Input autoFocus value={form.title} onChange={(e) => (set({ title: e.target.value }), setDuplicates(null))} maxLength={200} required />
        </Field>
        <Field label="Projeto">
          <Select value={form.projectKey} onChange={(e) => (set({ projectKey: e.target.value, module: '', labels: [], storyKey: '' }), setDuplicates(null))} required>
            <option value="">Selecione…</option>
            {projects.data?.map((p) => (
              <option key={p.key} value={p.key}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Módulo">
          <Select value={form.module} onChange={(e) => set({ module: e.target.value })} disabled={!project?.modules.length}>
            <option value="">{project?.modules.length ? 'Nenhum' : 'Projeto sem módulos'}</option>
            {project?.modules.map((m) => (
              <option key={m.key} value={m.key}>
                {m.name}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-1 gap-3 sm:col-span-2 sm:grid-cols-3">
          <Field label="Tipo">
            <Select value={form.type} onChange={(e) => set({ type: e.target.value })}>
              {TASK_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Prioridade">
            <Select value={form.priority} onChange={(e) => set({ priority: e.target.value })}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Status inicial">
            <Select value={form.status} onChange={(e) => set({ status: e.target.value })}>
              <option value="backlog">Backlog</option>
              <option value="todo">A Fazer</option>
              <option value="in_progress">Em Progresso</option>
            </Select>
          </Field>
        </div>
        {form.type === 'story' && (
          <Field label="Épico" hint="Objetivo maior ao qual esta história pertence (opcional)">
            <Select value={form.epicKey} onChange={(e) => set({ epicKey: e.target.value })}>
              <option value="">Nenhum</option>
              {epics.data?.items.map((ep) => (
                <option key={ep.key} value={ep.key}>
                  {ep.key} · {ep.title}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {!['story', 'epic'].includes(form.type) && (
          <Field label="História" hint="A funcionalidade à qual esta tarefa pertence (opcional)">
            <Select value={form.storyKey} onChange={(e) => set({ storyKey: e.target.value })} disabled={!form.projectKey}>
              <option value="">{form.projectKey ? 'Nenhuma' : 'Escolha o projeto'}</option>
              {stories.data?.items.map((st) => (
                <option key={st.key} value={st.key}>
                  {st.key} · {st.title}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Estimativa" hint={estimateInvalid ? undefined : estimateMinutes ? `= ${formatMinutes(estimateMinutes)}` : 'Ex.: 2h, 1h30, 45min'} error={estimateInvalid ? 'Use 2h, 1h30 ou 45min' : undefined}>
          <Input value={form.estimate} onChange={(e) => set({ estimate: e.target.value })} placeholder="2h" />
        </Field>
        <Field label="Responsável">
          <AssigneeSelect value={form.assignee} onChange={(v) => set({ assignee: v })} />
        </Field>
        <Field label="Início">
          <Input type="date" value={form.startDate} max={form.dueDate || undefined} onChange={(e) => set({ startDate: e.target.value })} />
        </Field>
        <Field label="Prazo">
          <Input type="date" value={form.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
        </Field>
        <Field label="Etiquetas" className="sm:col-span-2">
          <LabelPicker labels={labels.data ?? []} value={form.labels} onChange={(v) => set({ labels: v })} />
        </Field>
        <Field label="Descrição (Markdown)" className="sm:col-span-2">
          <Textarea rows={5} value={form.description} onChange={(e) => set({ description: e.target.value })} />
        </Field>
        <Field label="Critérios de aceite" hint="Um por linha" className="sm:col-span-2">
          <Textarea rows={3} value={form.criteria} onChange={(e) => set({ criteria: e.target.value })} />
        </Field>
        <Field label="Origem da demanda" hint="Ex.: pedido do cliente X, incidente, reunião" className="sm:col-span-2">
          <Input value={form.source} onChange={(e) => set({ source: e.target.value })} maxLength={500} />
        </Field>
        <button type="submit" hidden />
      </form>

      {duplicates && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3" role="alert">
          <p className="text-sm font-medium text-amber-900">Já existem tarefas parecidas neste projeto. Reutilize uma delas se for o mesmo trabalho:</p>
          <ul className="mt-2 space-y-1">
            {duplicates.map((d) => (
              <li key={d.key} className="flex items-center gap-2 text-sm">
                <Link to={`/tasks/${d.key}`} onClick={onClose} className="font-mono text-xs font-semibold text-navy-700 hover:underline">
                  {d.key}
                </Link>
                <span className="truncate">{d.title}</span>
                <StatusBadge status={d.status} />
                <span className="ml-auto text-xs text-amber-700">{Math.round(d.similarity * 100)}% parecida</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {create.error && !(create.error instanceof ApiError && create.error.code === 'possible_duplicates') && (
        <div className="mt-4">
          <ErrorState error={create.error} />
        </div>
      )}
    </Modal>
  );
}
